import { useEffect } from 'react';
import orderBy from 'lodash/orderBy';
import { useLiveQuery } from '@guuru/react-hooks';
import { FragmentOf, ResultOf } from '@guuru/graphql-common/gql';
import useCurrentUser from '../../../../../hooks/useCurrentUser';
import GET_EXPERT_PARTNERS from './queries/GetExpertPartners';
import EXPERT_PARTNERS_UPDATED from './subscriptions/ExpertPartnersUpdated';
import { PARTNER_FIELDS } from './fragments/PartnerFields';
import { SUBMISSION_FIELDS } from './fragments/SubmissionFields';
import { EXPERT_PARTNER_FIELDS } from './fragments/ExpertPartnerFields';

type Expert = ResultOf<typeof GET_EXPERT_PARTNERS>['expert'];

interface Scores {
  scores: NonNullable<Expert>['partners']['edges'][number]['scores'];
}

type PendingExpertPartner = FragmentOf<typeof SUBMISSION_FIELDS> & {
  node: FragmentOf<typeof PARTNER_FIELDS>;
  bucket: string;
};

type ExpertPartner = FragmentOf<typeof EXPERT_PARTNER_FIELDS> & Scores;

const useExpertPartners = function (skipSubscription: boolean = false) {
  const { id: expertId } = useCurrentUser()!;
  const {
    loading,
    refetch,
    fetchMore,
    data: { expert } = {},
  } = useLiveQuery(GET_EXPERT_PARTNERS, EXPERT_PARTNERS_UPDATED, {
    variables: { id: expertId },
    subscriptionOptions: { skip: skipSubscription },
  });

  const submissions = (expert?.submissions?.edges ?? []).map(({ node }) => {
    const { createdAt, partner } = node;

    return {
      node: partner,
      submittedAt: createdAt,
      bucket: 'pending',
    };
  });

  useEffect(() => {
    if (!skipSubscription) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const expertPartners = orderBy(
    [
      ...expert?.partners?.edges ?? [],
      ...submissions ?? [],
    ],
    'joinedAt',
    'desc',
  ) as Array<ExpertPartner | PendingExpertPartner>;

  return {
    expertPartners,
    fetchMore,
    pageInfo: expert?.partners?.pageInfo,
    submissions,
    loading,
  };
};

export default useExpertPartners;
